import { Injectable } from '@angular/core';
import { ShipmentService } from '../../../core/services/v3/shipment.service';
import { ConsignmentModel } from '../../consignments-v3/model/consignment.model';
import { BehaviorSubject, catchError, EMPTY, firstValueFrom, map, Observable, of, Subject, switchMap } from 'rxjs';
import { ResponseModel } from '../../../core/models/response.model';
import { DriverModel } from '../../../core/models/common-resource/driver.model';
import { CommonResourceService } from '../../../core/services/common-resource.service';
import { DataTableHeader } from '../model/datatable-header.model';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { NotyfService } from '../../../core/services/notyf.service';
import { appEnv } from '../../../app.config';
import moment, { Moment } from 'moment-timezone';
import { ShiftService } from '../../../core/services/v3/shift.service';
import _ from 'lodash';
import { RosterStatus, ShiftType, StopStatus } from '../../../core/models/status-and-type.enum';
import { RateScopeService } from '../../../core/services/v3/rate-scope.service';
import { RateScopeModel } from '../../../core/models/common-resource/rate-scope.model';
import { ShipmentSource } from '../../../core/models/app.enum';
@Injectable({
  providedIn: 'root'
})
export class DataService {

  private _consignments: ConsignmentModel[] = [];
  private _rawConsignments: ConsignmentModel[] = [];
  private _totalUnallocated: number = 0;
  private _dataTableHeader: DataTableHeader[] = [];
  private _rawTableHeader: DataTableHeader[] = [];
  filters: any = {
    tab: 'allocated',
    type: 2
  };
  public _activeTableSearchVal: string = '';
  activeConsignmentList: ConsignmentModel[] = [];

  private reservedDropOffPickupDetails = new Set<number>();

  private _availableMachshipTransaction: any[] = [];

  private _rateScopes: RateScopeModel[] = [];

  constructor(
    private shipmentService: ShipmentService,
    private commonResourceService: CommonResourceService,
    private notyf: NotyfService,
    private shiftService: ShiftService,
    private rateScopeService: RateScopeService,
  ) { this.generateTableHeader () }

  public get rateScopes () {
    return this._rateScopes;
  }

  /** Jobs use rate_scopes with shift_type_id=2 (State/Postcode/Suburb), unlike Shifts' shift_type_id=1 (Hourly/Per Stop/etc). */
  getRateScopes (shiftTypeId: number = 2) {
    const subject = new Subject<any>();
    this.rateScopeService.getAll(shiftTypeId).pipe(
      map((response: ResponseModel) => {
        if (response.success) {
          this._rateScopes = (response.data as Array<RateScopeModel>) || [];
        }
        return response;
      }),
      catchError(error => {
        subject.error(error || error?.error?.message || 'Something went wrong processing your request.');
        return EMPTY;
      })
    ).subscribe(response => {
      subject.next(response);
      subject.complete();
    });
    return subject;
  }

  public getAll (filterDate: {start_date: string, end_date: string} = {start_date: '', end_date: ''}, shiftTypeId: number | null = null): Observable<ResponseModel> {
    return this.shipmentService.getAll({ ...filterDate, shift_type_id: shiftTypeId }) as Observable<ResponseModel>;
  }

  public mapConsignments (payloadsData: any = []) {
    /* Reset pickup/dropoff details records as reference */
    this.reservedDropOffPickupDetails = new Set<number>();
    this._consignments = (payloadsData as Array<any>)
    .map(obj => ConsignmentModel.fromJson(obj))
    .map(obj => this.normalizeConsignment(obj))
    .map(obj => {
      const customerBranch = this.commonResourceService.customerBranch.find(client => client.id === obj.clientId);
      return Object.assign({
        ...obj,
        clientName: customerBranch ? customerBranch.clientName + ' - ' + customerBranch.name : '',
        stopStatusName: this.commonResourceService.stopStatus.find(stopStatus => stopStatus.id == obj.stopStatusId)?.name || '',
        stateName: this.commonResourceService.states.find(state => state.id == obj.pickUpStateId)?.abbr || '',
        travelSummary: this.generateTravelSummaryStr(obj),
      });
    }).map(obj => {
      if (obj.interestedContractors) {
        obj.interestedContractors = obj.interestedContractors.map((ic:any) => {
          const contractor = this.commonResourceService.drivers.find(c => c.id == ic.employee_id);
          if (!contractor) return false;
          return {
            id: ic.id,
            driverId: contractor.id,
            fullName: contractor.fullName,
            assignedVehicle: contractor.assignedVehicle.map(v => {
              return {
                id: v.id,
                name: v.name
              }
            }),
            roles: contractor.roles.map(r => r.name),
            empCode: contractor.employeeCode,
            profilePicture: contractor.profilePicture,
            selectedVehicleId: ic.vehicle_type_id,
            vehicleId: ic.vehicle_type_id ?? 0,
            roleId: ic.role_id ?? 0
          }
        }).filter((v:any) => v);
      }

      return obj;
    });
    this._rawConsignments = [...this._consignments];
  }

  public get consignments(): ConsignmentModel[] {
    return this._consignments;
  }

  public get rawConsignments (): ConsignmentModel[] {
    return this._rawConsignments;
  }

  public get drivers (): DriverModel[] {
    return this.commonResourceService.drivers;
  }
  
  public get totalUnallocated(): number {
    return this.rawConsignments?.filter(consignment => {
      const shift = consignment.shift;
      if (!shift) return true;
      return shift.assigned_contractors.find((contractor: any) => Boolean(contractor.vehicle_type_id) === false);
    }).length;
  }

  public get dataTableHeader () {
    return this._dataTableHeader;
  }

  public get rawTableHeader () {
    return this._rawTableHeader;
  }

  public get stopStatus () {
    return this.commonResourceService.stopStatus;
  }

  public get serviceTypes () {
    return this.commonResourceService.serviceTypes;
  }

  generateTableHeader () {
    this._dataTableHeader = this._rawTableHeader = [
      new DataTableHeader({name: '', width: 2}),
      new DataTableHeader({name: '', width: 1}),
      new DataTableHeader({key: "statusName", name: "Status", sort: '', sortType: '', width: 130, align: 'center'}), //stop status
      new DataTableHeader({key: "consignmentNumber", name: "Consignment", sort: '', sortType: '', width: 150, align: 'center'}),
      new DataTableHeader({key: "pickup", name: "Dispatch", sort: '', sortType: '', width: 80, align: 'center'}),
      new DataTableHeader({key: "dropoff", name: "SLA", sort: '', sortType: '', width: 85, align: 'center'}),
      new DataTableHeader({key: "eta", name: "ETA", sort: '', sortType: '', width: 70, align: 'center'}),
      new DataTableHeader({key: "travelSummary", name: "Travel Summary", sort: '', sortType: '', width: 150, align: 'center'}),
      new DataTableHeader({key: "stateName", name: "State", sort: '', sortType: '', width: 60, align: 'center'}),
      new DataTableHeader({key: "clientName", name: "Client", sort: '', sortType: '', width: 150, align: 'center'}),
      new DataTableHeader({key: "serviceName", name: "Service Name", sort: '', sortType: '', width: 120, align: 'center'}),
      new DataTableHeader({key: "addOns", name: "Add-on", sort: '', sortType: '', width: 100, align: 'center'}),
      new DataTableHeader({key: "assignee", name: "Assignee", sort: '', sortType: '', width: 200, align: 'center'}),
      new DataTableHeader({key: "route", name: "Route", sort: '', sortType: '', width: 60, align: 'center'}),
      new DataTableHeader({key: "pod", name: "POD", sort: '', sortType: '', width: 60, align: 'center'}),
      new DataTableHeader({key: "signature", name: "Signature", sort: '', sortType: '', width: 70, align: 'center'}),
      new DataTableHeader({key: "tailgate", name: "Tailgate", sort: '', sortType: '', width: 90, align: 'center'}),
      new DataTableHeader({key: "jockey", name: "Jockey", sort: '', sortType: '', width: 90, align: 'center'}),
      new DataTableHeader({key: "sell", name: "Sell", sort: '', sortType: '', width: 90, align: 'center'}),
      new DataTableHeader({key: "cost", name: "Cost", sort: '', sortType: '', width: 90, align: 'center'}),
      new DataTableHeader({key: "grossProfit", name: "Gross Profit", sort: '', sortType: '', width: 150, align: 'center'}),
      new DataTableHeader({key: "source", name: "Source", sort: '', sortType: '', width: 130, align: 'center'})
    ];
  }

  filterByTab (tab: 'all' | 'allocated' | 'unallocated', consignments: ConsignmentModel[] = []) {
    this.filters.tab = tab;
    const type = this.filters.type;
    return consignments.filter(c => {
      if (tab == 'all') return c;
      const statusId = c.stopStatusId;
      if (tab == 'allocated') {
        return c.shift ? ![RosterStatus.CANCELLED, RosterStatus.UNPAID].includes(c.shift?.shift_status_id) : (statusId !== StopStatus.CANCELLED)
      } else if (tab == 'unallocated') {
        return c.shift ? ![RosterStatus.CANCELLED, RosterStatus.UNPAID].includes(c.shift?.shift_status_id) : (statusId === StopStatus.PENDING)
      }
      return false;
    }).filter(c => {
      const contractors = (c.shift ? c.shift.assigned_contractors : c.consignmentContractors) ?? [];
      if (tab == 'all') return c;
      if (tab == 'allocated') return contractors.length;
      if (tab == 'unallocated') return contractors.length === 0;
    }).filter(c => {
      /* shiftId (not shift) - see filterByContentType for why. */
      if (type == 0) return c;
      else if (type == 1) return !!c.shiftId;
      else if (type == 2) return !c.shiftId;
      return false;
    });
  }

  emptyConsignments () {
    this._consignments = this._rawConsignments = [];
  }

  filterBySearch (value: string = '', consignments: ConsignmentModel[] | any[] = []) {
    const _value = value.toLowerCase().trim();
    this._activeTableSearchVal = _value;
    if (_value == '') return consignments;
    return consignments.filter(consignment => {
      const customerBranch = this.commonResourceService.customerBranch.find(branch => branch.id == consignment.clientId);
      const clientUniqId = customerBranch?.uniqId ?? 0;
      let customerName = '';
      if (clientUniqId) {
        const customerBranch = this.commonResourceService.customerBranch.find(customerBranch => customerBranch.uniqId == clientUniqId);
        if (customerBranch) {
          customerName = `${customerBranch.clientName}-`;
        }
      }
      const isInCN = String(consignment.consignmentNumber).toLowerCase().includes(_value);
      const isInClient = String(consignment.clientName).toLowerCase().includes(_value);
      const isInShiftId = String(customerName+consignment.shiftId).toLowerCase().includes(_value);
      const isInShiftShipment = String(consignment.id).toLowerCase().includes(_value) || String(consignment.shiftId).includes(_value);
      let isInDriver = false;
      // let isInDriverEmpCode = false;
      // let isInDriverRoleName = false;
      // let isInDriverAssignedVehicle = false;

      const contractors: any[] = ((this.filters.type == ShiftType.SHIFT) ?
       consignment?.shift?.assigned_contractors : (this.filters.type == ShiftType.JOBS ? consignment.consignmentContractors : (consignment?.shift?.assigned_contractors || consignment.consignmentContractors))) || [];
      if (contractors.length > 0) {

        isInDriver = contractors.some(contractor => {
          const driverId = contractor.employee_id ?? contractor.driver_id;
          const driverInfo = this.commonResourceService.drivers.find(_driver => _driver.id == driverId);
          const isInDriverName = driverInfo?.fullName?.toLowerCase().includes(_value);
          const isInDriverEmpCode = driverInfo?.employeeCode?.toLowerCase().includes(_value);
          const isInDriverRolename = driverInfo?.roles.length ? driverInfo?.roles.some(role => (role.name.toLowerCase()).includes(_value)) : false;

          const vehicleName = this.commonResourceService.vehicles.find(vehicle => vehicle.id == contractor.vehicle_type_id)?.name ?? '';
          const isInDriverAssignedVehicle = vehicleName.toLowerCase().includes(_value);

          return isInDriverName || isInDriverEmpCode || isInDriverRolename || isInDriverAssignedVehicle;
        });
      }
      return isInCN || isInClient || isInDriver || isInShiftId || isInShiftShipment;
    });
  }

  filterByColumn (filterValue: any, isChecked: boolean = true, multiFilter: any = [], consignments: ConsignmentModel[] = []): ConsignmentModel[] {
    return consignments.filter(consignment => {
      const stopStatusName = this.commonResourceService.stopStatus.find(stopStatus => stopStatus.id == consignment.stopStatusId)?.name || '';
      const isInStatus = filterValue['statusName'].value.length === 0 ? true : filterValue['statusName'].value.some((status: string) => String(status).toLowerCase().includes(stopStatusName.toLowerCase()));
      const isInConsignment = filterValue['consignmentNumber'].value.length === 0 ? true : String(filterValue['consignmentNumber'].value[0]).includes(String(consignment.consignmentNumber).toLowerCase());
      const isInCustomerBranch = filterValue['clientName'].value.length === 0 ? true : filterValue['clientName'].value.some((customerBranch: string) => customerBranch.includes(String(consignment.clientId)));

      const shipmentPickupStop = consignment.pickupStop;
      const pickupStateId = (this.commonResourceService.states.find(state => state.id == shipmentPickupStop?.state_id)?.id || '');
      const isInState = filterValue['stateName'].value.length === 0 ? true : shipmentPickupStop && filterValue['stateName'].value.includes(String(pickupStateId));

      const isInServiceName = filterValue['serviceName'].value.length === 0 ? true : filterValue['serviceName'].value.some((serviceId: string) => serviceId.includes(String(consignment.serviceTypeId)));

      const driverFilter: any[] = multiFilter.drivers;
      const rolesFilter: number[] = multiFilter.roles;
      const vehiclesFilter: number[] = multiFilter.vehicles;

      let isInDriverFilter = driverFilter.length === 0;
      let isInDriverRole = rolesFilter.length === 0;
      let isInDriverVehicleType = vehiclesFilter.length === 0;

      const shift = consignment.shift;
      const contractors: any[] = (shift ? shift.assigned_contractors : consignment.consignmentContractors) || [];
      if (contractors.length) {
        const contractorIds = contractors.map(contractor => contractor.driver_id || contractor.employee_id);
        const roleIds = contractors.map(contractor => contractor.role_id);
        const vehicleIds = contractors.map(contractor => contractor.vehicle_type_id);
        if (!isInDriverFilter) {
          isInDriverFilter = driverFilter.some(driverId => contractorIds.includes(driverId));
        }
        if (!isInDriverRole) {
          isInDriverRole = rolesFilter.some(roleId => roleIds.includes(roleId));
        }
        if (!isInDriverVehicleType) {
          isInDriverVehicleType = vehiclesFilter.some(vehicleId => vehicleIds.includes(vehicleId));
        }
      }

      let isInRouteId = filterValue['route'].value.length == 0;

      const customId = String(consignment.shift ? `${consignment.shift.id}` : `${consignment.id}`);
      if (!isInRouteId) {
        const routeIds = filterValue['route'].value.map((obj:any) => {
          return obj.replace('shift-','').replace('job-','');
        });
        isInRouteId = routeIds.includes(customId);
      }

      let isInDispatchDateRange = filterValue['pickup'].value.length == 0;
      let isPickUpTimeStrict = filterValue['pickup'].strictPickUp ? consignment.pickUpTimesStrict : true;
      let isInETADateRange = filterValue['dropoff'].value.length == 0;
      let isInNewEtaDateRange = filterValue['eta']?.value?.length == 0;
      
      if (filterValue['pickup'].value.length != 0 && !isInDispatchDateRange) {
        const earliestPickup: Moment | string = moment.utc(consignment.earliestPickup);
        const latestPickup: Moment | string = moment.utc(consignment.latestPickup);
        const filterStart: Moment | string = moment.utc(filterValue['pickup'].value[0]);
        const filterEnd: Moment | string = moment.utc(filterValue['pickup'].value[1]);
        if (earliestPickup.isValid() && filterStart.isValid() && filterEnd.isValid()) {
          const c1 = filterStart.isBetween(earliestPickup, latestPickup, 'date');
          const c2 = filterEnd.isBetween(earliestPickup, latestPickup, 'date');
          isInDispatchDateRange = earliestPickup.isBetween(filterStart, filterEnd, 'date') || earliestPickup.isSame(filterStart, 'date') || earliestPickup.isSame(filterEnd, 'date');
        }
      }

      if (filterValue['dropoff'].value.length != 0 && !isInETADateRange) {
        const earliestDropOff: Moment | string = moment.utc(consignment.earliestDropOff);
        const latestDropOff: Moment | string = moment.utc(consignment.latestDropOff);
        const filterStart: Moment | string = moment.utc(filterValue['dropoff'].value[0]);
        const filterEnd: Moment | string = moment.utc(filterValue['dropoff'].value[1]);
        if (earliestDropOff.isValid() && latestDropOff.isValid() && filterStart.isValid() && filterEnd.isValid()) {
          const c1 = earliestDropOff.isBetween(filterStart, filterEnd, 'date', '[]');
          const c2 = latestDropOff.isBetween(filterStart, filterEnd, 'date', '[]');
          isInETADateRange = c1 || c2;
        }
      }

      if (filterValue['eta']?.value?.length != 0 && !isInNewEtaDateRange) {
        const eta: Moment | string = moment.utc(consignment.eta);
        const filterStart: Moment | string = moment.utc(filterValue['eta'].value[0]);
        const filterEnd: Moment | string = moment.utc(filterValue['eta'].value[1]);

        if (eta.isValid() && filterStart.isValid() && filterEnd.isValid()) {
          isInNewEtaDateRange = eta.isBetween(filterStart, filterEnd, 'date', '[]');
        } else {
          isInNewEtaDateRange = false;
        }
      }

      /* Set to isInRoute to true regardless if the shift is empty or not for 'unallocate' tab only */
      return isInRouteId && isInStatus && isInConsignment && isInCustomerBranch && isInState && isInServiceName && isInDriverFilter && isInDriverRole && isInDriverVehicleType && isInDispatchDateRange && isPickUpTimeStrict && isInETADateRange && isInNewEtaDateRange;
    })
    .map(obj => {
      const stopStatusName = this.commonResourceService.stopStatus.find(stopStatus => stopStatus.id == obj.stopStatusId)?.name || '';
      return {...obj,...{
        stopStatusName: stopStatusName
      }}
    });
  }

  filterByContentType (type: number, consignments: ConsignmentModel[] = []) {
    /* shiftId is a plain field present from the initial getAll() response - unlike shift (the full
       object), it doesn't depend on the async attachConsignmentShift() follow-up call resolving first,
       so a consignment merged into a Shift is correctly bucketed even before that call completes. */
    return consignments.filter(c => {
      if (type == ShiftType.SHIFT) return !!c.shiftId;
      if (type == ShiftType.JOBS) return !c.shiftId;
      return c;
    })
  }

  sortDirCol (filterValue: any, isReset = false, colKey = '', sortDir: 'asc' | 'desc' | '', multiFilter: any = []) {
    let raw = [...this.rawConsignments];
    if (isReset) {
      this._consignments = raw;
      return this.filterByColumn(filterValue, false, multiFilter);
    }
    this._consignments = this.activeConsignmentList.map(obj => {
      return {
        ...obj,
        customId: this.filters.type == ShiftType.SHIFT ? obj.shift.id : obj.id
      }
    }).sort((a:any,b:any) => {
      let aVal = String(a[colKey] || '');
      let bVal = String(b[colKey] || '');
      if (colKey == 'assignee') {
        let aAssignee = a.shift?.assigned_contractors;
        let bAssignee = b.shift?.assigned_contractors;
        if (aAssignee.length > 0) {
          aAssignee = (aAssignee as Array<any>).find(contractor => contractor.vehicle_type_id != 0);
          aVal = this.commonResourceService.drivers.find(driver => driver.id == aAssignee.driver_id)?.fullName || '';
        }
        if (bAssignee.length > 0) {
          bAssignee = (bAssignee as Array<any>).find(contractor => contractor.vehicle_type_id != 0);
          bVal = this.commonResourceService.drivers.find(driver => driver.id == bAssignee.driver_id)?.fullName || '';
        }
      }
      if (!['pickup','dropoff','eta'].includes(colKey)) {
        if (colKey == 'serviceName') {
          const aServiceType = this.commonResourceService.serviceTypes.find(serviceType => serviceType.id == a.serviceTypeId);
          if (aServiceType) {
            const category = this.commonResourceService.serviceCategories.find(category => category.id == aServiceType.categoryId)?.name || '';
            aVal = `${category}-${aServiceType.name}`;
          }
          const bServiceType = this.commonResourceService.serviceTypes.find(serviceType => serviceType.id == b.serviceTypeId);
          if (bServiceType) {
            const category = this.commonResourceService.serviceCategories.find(category => category.id == bServiceType.categoryId)?.name || '';
            bVal = `${category}-${bServiceType.name}`;
          }
        }
        if (sortDir == 'asc') {
          return aVal.localeCompare(bVal);
        } else if (sortDir == 'desc') {
          return bVal.localeCompare(aVal);
        }
      } else {
        const aDate = new Date(
          colKey == 'pickup' ? a.earliestPickup :
          colKey == 'dropoff' ? a.earliestDropOff :
          a.eta
        ).getTime();
        const bDate = new Date(
          colKey == 'pickup' ? b.earliestPickup :
          colKey == 'dropoff' ? b.earliestDropOff :
          b.eta
        ).getTime();
        if (sortDir == 'asc') {
          return aDate - bDate;
        } else if (sortDir == 'desc') {
          return bDate - aDate;
        }
      }
      return 0;
    });

    return this._consignments;
  }

  private normalizeConsignment(obj: any): any {
    if (!obj) return obj;

    // Match Route Planner behaviour: backend can return pickUpStop but UI expects pickupStop
    if (obj.pickUpStop && !obj.pickupStop) obj.pickupStop = obj.pickUpStop;
    if (obj.dropOffStop && !obj.dropoffStop) obj.dropoffStop = obj.dropOffStop;

    // Keep both variants for compatibility (some screens/components may still read the old keys)
    if (obj.pickupStop && !obj.pickUpStop) obj.pickUpStop = obj.pickupStop;
    if (obj.dropoffStop && !obj.dropOffStop) obj.dropOffStop = obj.dropoffStop;

    // Pickup contact key mismatch (your table uses the misspelled one)
    const pickupContact =
      obj.pickUpStopContactInformation ??
      obj.pickUpStopContactInformmation ??
      obj.pickupStopContactInformation ??
      obj.pickupStopContactInformmation ??
      '';

    obj.pickUpStopContactInformation = pickupContact;
    obj.pickUpStopContactInformmation = pickupContact;
    obj.pickupStopContactInformation = pickupContact;
    obj.pickupStopContactInformmation = pickupContact;

    // Dropoff contact variants
    const dropoffContact =
      obj.dropOffStopContactInformation ??
      obj.dropoffStopContactInformation ??
      '';

    obj.dropOffStopContactInformation = dropoffContact;
    obj.dropoffStopContactInformation = dropoffContact;

    return obj;
  }

  generateTravelSummaryStr (consignment: ConsignmentModel) {
    let travelDistanceTime = '';
    const pickupStopSuburb = consignment?.pickupStop?.suburb || '';
    const dropoffStopSuburb = consignment?.dropoffStop?.suburb || '';
    travelDistanceTime = `${this.formatTravelTime(consignment?.travelTime)} ↔ ${this.formatTravelDistanceKm(consignment?.distance)}`
    return `${pickupStopSuburb} → ${dropoffStopSuburb}|${travelDistanceTime}`;
  }

  convertMinutesToTime(totalMinutes = 0) {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    let timeString = '';

    if (hours > 0) {
      timeString += `${hours} hr${hours > 1 ? 's' : ''}`;
    }

    if (minutes > 0) {
      timeString += `${hours > 0 ? ' ' : ''}${minutes} min${minutes > 1 ? 's' : ''}`;
    }
    
    if (totalMinutes === 0) {
        return '0 min';
    }

    return timeString.trim();
  }

  updateShipmentStopStatus (consignmentId: number = 0, statusId: number = 0) {
    let subj = new Subject<any>;

    this.shipmentService.updateStopStatus(consignmentId, statusId).pipe(
      map((response: ResponseModel) => {
        return response;
      }),
      catchError (error => {
        subj.error(error.error.message || error.message || "Something went wrong processing your request");
        return [];
      })
    ).subscribe(response => {
      this._rawConsignments.forEach(consignment => {
        if (consignment.id != consignmentId) return;
        consignment.stopStatusId = statusId;
      });
      this._consignments.forEach(consignment => {
        if (consignment.id != consignmentId) return;
        consignment.stopStatusId = statusId;
      })
      subj.next(response);
      subj.complete();
    })

    return subj;
  }

  updateShipmentServiceType (consignmentId: number = 0, serviceTypeId: number = 0) {
    let subj = new Subject<any>;

    this.shipmentService.updateServiceType(consignmentId, serviceTypeId).pipe(
      map((response: ResponseModel) => {
        return response;
      }),
      catchError (error => {
        subj.error(error.error.message || error.message || "Something went wrong processing your request");
        return [];
      })
    ).subscribe(response => {
      this._rawConsignments = this._rawConsignments.map((consignment:any) => {
        if (consignment.id == consignmentId) {
          const serviceName = this.commonResourceService.serviceTypes.find(service => service.id == serviceTypeId)?.name || '';
          consignment.serviceTypeId = serviceTypeId;
          consignment.serviceTypeName = serviceName;
        }
        return consignment;
      });
      this._consignments = this._consignments.map((consignment: any) => {
        if (consignment.id == consignmentId) {
          const serviceName = this.commonResourceService.serviceTypes.find(service => service.id == serviceTypeId)?.name || '';
          consignment.serviceTypeId = serviceTypeId;
          consignment.serviceTypeName = serviceName;
        }
        return consignment;
      });
      subj.next(response);
      subj.complete();
    })

    return subj;
  }

  updateShipmentTailgate (consignmentId: number = 0, tailgate: number = 0) {
    let subj = new Subject<any>;

    this.shipmentService.updateShipmentTailgate(consignmentId, tailgate).pipe(
      map((response: ResponseModel) => {
        return response;
      }),
      catchError (error => {
        subj.error(error.error.message || error.message || "Something went wrong processing your request");
        return [];
      })
    ).subscribe(response => {
      this._rawConsignments = this._rawConsignments.map((consignment:any) => {
        if (consignment.id == consignmentId) {
          consignment.tailgate = tailgate;
        }
        return consignment;
      });
      this._consignments = this._consignments.map((consignment: any) => {
        if (consignment.id == consignmentId) {
          consignment.tailgate = tailgate;
        }
        return consignment;
      });
      subj.next(response);
      subj.complete();
    })

    return subj;
  }

  updateShipmentJockey (consignmentId: number = 0, jockey: number = 0) {
    let subj = new Subject<any>;

    this.shipmentService.updateShipmentJockey(consignmentId, jockey).pipe(
      map((response: ResponseModel) => {
        return response;
      }),
      catchError (error => {
        subj.error(error.error.message || error.message || "Something went wrong processing your request");
        return [];
      })
    ).subscribe(response => {
      this._rawConsignments = this._rawConsignments.map((consignment:any) => {
        if (consignment.id == consignmentId) {
          consignment.jockey = jockey;
        }
        return consignment;
      });
      this._consignments = this._consignments.map((consignment: any) => {
        if (consignment.id == consignmentId) {
          consignment.jockey = jockey;
        }
        return consignment;
      });
      subj.next(response);
      subj.complete();
    })

    return subj;
  }

  deleteShift (data: ConsignmentModel) {
    let subj = new Subject<any>;

    this.shipmentService.deleteShift(data.shiftId).pipe(
      map((response: ResponseModel) => {
        return response;
      }),
      catchError (error => {
        subj.error(error.error.message || error.message || "Something went wrong processing your request");
        return [];
      })
    ).subscribe(response => {
      this._rawConsignments.forEach(consignment => {
        if (data.id == consignment.id) {
          consignment.shift = null;
          consignment.shiftId = 0;
        }
      });
      this._consignments.forEach(consignment => {
        if (data.id == consignment.id) {
          consignment.shift = null;
          consignment.shiftId = 0;
        }
      });
      subj.next(response);
      subj.complete();
    });

    return subj;
  }

  bulkDeleteConsignment (consignmentIds: number[] = []) {
    let subj = new Subject<any>;
    this.shipmentService.bulkDeleteConsignment(consignmentIds).pipe(
      map((response: ResponseModel) => {
        return response;
      }),
      catchError (error => {
        subj.error(error.error.message || error.message || "Something went wrong processing your request");
        return [];
      })
    ).subscribe(response => {
      this._rawConsignments = this._rawConsignments.filter(rawConsignment => !consignmentIds.includes(rawConsignment.id));
      this._consignments = this._rawConsignments
      subj.next(response);
      subj.complete();
    });

    return subj;
  }

  uploadPOD (consignment: ConsignmentModel, payloads: any) {
    let subj = new Subject<any>
    const id = consignment.id;
    this.shipmentService.uploadPOD(id, {pod: payloads}).pipe(
      map((response: ResponseModel) => {
        return response;
      }),
      catchError (error => {
        subj.error(error.error.message || error.message || "Something went wrong processing your request");
        return [];
      })
    ).subscribe(response => {
      if (response?.success) {
        this._rawConsignments = this._rawConsignments.map(_consignment => {
          if (_consignment.id == id) {
            consignment.proofOfDelivery = response.data.proof_of_delivery;
          }
          return _consignment;
        });
        this._consignments = this._consignments.map(_consignment => {
          if (_consignment.id == id) {
            _consignment.proofOfDelivery = response.data.proof_of_delivery;
          }
          return _consignment;
        });
      }
      subj.next(response);
      subj.complete();
    });
    return subj;
  }

  async downloadByZip(consignment: any, podKeys: any) {
    if (!consignment?.proofOfDelivery) {
      this.notyf.notifyError('No Proof Of Delivery photos found.');
      return false;
    }

    if (podKeys.length > 5) {
      this.notyf.notifyError('Unable to download your proof of delivery at the moment.');
      return false;
    }

    const _podKeys = Object.keys(consignment.proofOfDeliveryObj);
    const zip = new JSZip();
    const folder = zip.folder("pod");
    const podPhotos = _podKeys.map(key => {
      if (typeof consignment.proofOfDeliveryObj[key] !== 'undefined' && consignment.proofOfDeliveryObj[key]) return consignment.proofOfDeliveryObj[key];
    }).filter(path => path.startsWith('pod/'));
    const fetchPromises = podPhotos.map(async (image, index) => {
      const _fileName = podKeys[index];
      if (!image || !_fileName) return false;

      try {
        const response: any = await firstValueFrom(this.shipmentService.getPODB64(image));
        
        const blob = this.base64ToBlob(response.data, 'application/octet-stream');
        const ext = image.replace(`${appEnv.s3Url}`, '').split('.')[1] || 'jpg';
        
        folder?.file(`${_fileName}.${ext}`, blob);
        return true;
      } catch (error) {
        console.error(`Failed to download POD for index ${index}:`, error);
        return false;
      }
    });

    const results = await Promise.all(fetchPromises);
    const downloadedCount = results.filter(success => success === true).length;

    if (downloadedCount === 0) {
      this.notyf.notifyError('Failed to download Proof Of Delivery photos, please try again.');
      return false;
    }

    const content = await zip.generateAsync({ type: 'blob' });
    saveAs(content, `${moment().format('YYYYMMDD')}POD_${consignment.consignmentNumber ?? consignment.id}.zip`);
    
    this.notyf.notifySuccess('Successfully downloaded Proof of Delivery photos');
    return true;
  }

  private base64ToBlob(base64: string, contentType: string): Blob {
    const base64Data = base64
      .replace(/^data:image\/jpeg;base64,/, '')
      .replace(/^data:image\/png;base64,/, '')
      .replace(/^data:jpg;base64,/, '')
      .replace(/^data:jpeg;base64,/, '')
      .replace(/^data:png;base64,/, '');
    const byteCharacters = atob(base64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }
    return new Blob(byteArrays, { type: contentType });
  }

  bulkUpdateStopStatus (ids: number[] = [], statusId: number = 0) {
    let subj = new Subject<any>;
    const dateTimeString = moment().format('YYYY-MM-DD HH:mm:ss');
    this.shipmentService.bulkUpdateConsignmentStatus(ids, statusId, dateTimeString).pipe(
      map((response: ResponseModel) => {
        return response;
      }),
      catchError (error => {
        subj.error(error.error.message || error.message || "Something went wrong processing your request");
        return [];
      })
    ).subscribe(response => {
      const _date = moment().format('YYYY-MM-DD');
      this._rawConsignments = this._rawConsignments.map(rawConsignment => {
        if (ids.includes(rawConsignment.id)) {
          rawConsignment.stopStatusId = statusId;
          switch(statusId) {
            case 1:
              rawConsignment.datePending = _date;
            break;
            case 2:
              rawConsignment.dateIntransit = _date;
            break
            case 3:
              rawConsignment.dateSuccessful = _date;
            break;
            case 4:
              rawConsignment.dateFailed = _date;
            break;
            case 5:
              rawConsignment.dateSkipped = _date;
            break;
            case 6:
              rawConsignment.dateSkipped = _date;
            break;
          }
        }
        return rawConsignment
      });
      this._consignments = this._consignments.map(consignment => {
        if (ids.includes(consignment.id)) {
          consignment.stopStatusId = statusId;
          switch(statusId) {
            case 1:
              consignment.datePending = _date;
            break;
            case 2:
              consignment.dateIntransit = _date;
            break
            case 3:
              consignment.dateSuccessful = _date;
            break;
            case 4:
              consignment.dateFailed = _date;
            break;
            case 5:
              consignment.dateSkipped = _date;
            break;
            case 6:
              consignment.dateSkipped = _date;
            break;
          }
        }
        return consignment;
      });
      subj.next(response);
      subj.complete();
    });
    return subj;
  }

  bulkUpdateServiceType(ids: number[] = [], serviceId: number = 0) {
    let subj = new Subject<any>;
    this.shipmentService.bulkUpdateServiceType(ids, serviceId).pipe(
      map((response: ResponseModel) => {
        return response;
      }),
      catchError (error => {
        subj.error(error.error.message || error.message || "Something went wrong processing your request");
        return [];
      })
    ).subscribe(response => {
      this._rawConsignments.forEach(rawConsignment => {
        if (!ids.includes(rawConsignment.id)) return;
        rawConsignment.serviceTypeId = serviceId;
      });
      this._consignments.filter(consignment => {
        if (!ids.includes(consignment.id)) return;
        consignment.serviceTypeId = serviceId;
      });
      subj.next(response);
      subj.complete();
    });

    return subj;
  }

  bulkRemoveAttachedShift (ids: number[] = []) {
    let subj = new Subject<any>;
    this.shipmentService.bulkRemoveConsignmentShift(ids).pipe(
      map((response: ResponseModel) => {
        return response;
      }),
      catchError (error => {
        subj.error(error.error.message || error.message || "Something went wrong processing your request");
        return [];
      })
    ).subscribe(response => {
      this._rawConsignments = this._rawConsignments.map(rawConsignment => {
        if (ids.includes(rawConsignment.id)) {
          rawConsignment.shiftId = 0;
          rawConsignment.shift = null;
        }
        return rawConsignment;
      });
      this._consignments = this._consignments.filter(consignment => {
        if (ids.includes(consignment.id)) {
          consignment.shiftId = 0;
          consignment.shift = null;
        }
        return consignment
      });
      subj.next(response);
      subj.complete();
    });

    return subj;
  }

  removePOD (id: number = 0, data: any) {
    let subject = new Subject<any>();
    this.shipmentService.updatePOD(id, data).pipe(
      map((response: ResponseModel) => {
        return response;
      }), catchError (error => {
        subject.error(error.error.message || error.message || "Something went wrong processing your request");
        return [];
      })
    ).subscribe(response => {
      subject.next(response);
      subject.complete();
    });
    return subject;
  }

  saveConsignmentContractors (data: any = []) {
    let subject = new Subject<any>();
    this.shipmentService.assignContractor(data).pipe(
      switchMap((response: ResponseModel) => {
        let assigned = false;
        let c: any = null;
        this._rawConsignments = this._rawConsignments.map(rawConsignment => {
          if (rawConsignment.id == data.stop_id) {
            const isExists = rawConsignment.consignmentContractors.find(obj => obj.employee_id == data.employee_id);
            if (!isExists) {
              rawConsignment.consignmentContractors.push(response.data);
              assigned = true;
            }
          }
          return rawConsignment;
        })
        this._consignments = this._consignments.map(consignment => {
          if (consignment.id == data.stop_id) {
            c = consignment;
            const isExists = consignment.consignmentContractors.find(obj => obj.employee_id == data.employee_id);
            if (!isExists) {
              consignment.consignmentContractors.push(response.data);
            }
          }
          return consignment;
        });
        /* A freshly-assigned driver has no computed rate yet — recalculate so the table's Sell/Cost reflect it immediately. */

        //uncomment once need to recalculate after assigning of contractor
        // if (c.sourceId == ShipmentSource.ADMIN_PANEL) { //re-calculate only apply for admin-panel data
        //   this.calculateConsignmentRates(data.stop_id)
        // }
        return of(response);
      }), catchError (error => {
        subject.error(error?.error?.message || error?.message || "Something went wrong processing your request");
        return [];
      })
    ).subscribe(response => {
      subject.next(response);
      subject.complete();
    });
    return subject;
  }

  saveConsignmentShiftContractor (consignmentId: number = 0, data: any = []) {
    let subject = new Subject<any>();
    this.shiftService.assignContractor(data.shift_id, data.driver_id, data.vehicle_type_id, data.role_id, 1,'',consignmentId).pipe(
      switchMap((response: ResponseModel) => {
        const shiftId = this._consignments.find(obj => obj.id == consignmentId)?.shiftId;
        let newAssigneeId: number | null = null;
        this._rawConsignments = this._rawConsignments.map(rawConsignment => {
          if (rawConsignment.shiftId == shiftId) {
            const isExists = rawConsignment.shift.assigned_contractors?.find((obj:any) => obj.driver_id == data.driver_id);
            if (!isExists) {
              rawConsignment.shift.assigned_contractors?.push(response.data);
              newAssigneeId = response.data?.id;
            }
          }
          return rawConsignment;
        })
        this._consignments = this._consignments.map(consignment => {
          if (consignment.shiftId == shiftId) {
            const isExists = consignment.shift.assigned_contractors?.find((obj:any) => obj.driver_id == data.driver_id);
            if (!isExists) {
              consignment.shift.assigned_contractors?.push(response.data);
            }
          }
          return consignment;
        })
        /* A freshly-assigned driver has no computed rate yet — recalculate so the table's Sell/Cost reflect it immediately. */
        return newAssigneeId ? this.calculateShiftRates([newAssigneeId]).pipe(map(() => response), catchError(() => of(response))) : of(response);
      }), catchError (error => {
        subject.error(error.error.message || error.message || "Something went wrong processing your request");
        return [];
      })
    ).subscribe(response => {
      subject.next(response);
      subject.complete();
    });
    return subject;
  }

  removeShiftAssignContractors (id: number = 0, consignmentId: number = 0) {
    let subject = new Subject<any>;
    this.shiftService.removeContractor(id, consignmentId).pipe(
      map((response: ResponseModel) => {
        return response;
      }), catchError(error => {
        subject.error(error.error.message || error.message || "Something went wrong processing your request");
        return [];
      })
    ).subscribe(response => {
      const shiftId = this._consignments.find(obj => obj.id == consignmentId)?.shiftId;
      this._consignments = this._consignments.map(consignment => {
        if (consignment.shiftId == shiftId) {
          const contractors = [...consignment.shift.assigned_contractors].filter((obj:any) => {
            return obj.id != id;
          });
          const shift = _.omit(consignment.shift, ['assigned_contractors']);
          const _shift = {...shift,...{assigned_contractors: contractors}};
          consignment.shift = _shift
        }
        return consignment;
      });

      this._rawConsignments = this._rawConsignments.map(rawConsignment => {
        if (rawConsignment.shiftId == shiftId) {
          const contractors = [...rawConsignment.shift.assigned_contractors].filter((obj:any) => {
            return obj.id != id;
          });
          const shift = _.omit(rawConsignment.shift, ['assigned_contractors']);
          const _shift = {...shift,...{assigned_contractors: contractors}};
          rawConsignment.shift = _shift
        }
        return rawConsignment;
      });
      subject.next(response);
      subject.complete();
    });
    return subject;
  }

  removeConsignmentContractors (id: number = 0, consignmentId: number = 0) {
    let subject = new Subject<any>;
    this.shipmentService.removeContractor(id).pipe(
      map((response: ResponseModel) => {
        return response;
      }), catchError(error => {
        subject.error(error.error.message || error.message || "Something went wrong processing your request");
        return [];
      })
    ).subscribe(response => {
      this._consignments = this._consignments.map(consignment => {
        if (consignment.id == consignmentId) {
          consignment.consignmentContractors = consignment.consignmentContractors.filter(contractor => {
            return contractor.id != id;
          });
        }
        return consignment;
      });

      this._rawConsignments = this._rawConsignments.map(rawConsignment => {
        if (rawConsignment.id == consignmentId) {
          rawConsignment.consignmentContractors = rawConsignment.consignmentContractors.filter(contractor => {
            return contractor.id != id;
          });
          return rawConsignment;
        }
        return rawConsignment;
      });
      subject.next(response);
      subject.complete();
    });
    return subject;
  }

  createMergedConsignmentShift (data: any) {
    let subject = new Subject<any>;
    this.shipmentService.createShiftWithMergedConsignment(data).pipe(
      map((response: ResponseModel) => {
        return response
      }), catchError(error => {
        subject.error(error.error.message || "Something went wrong processing your request");
        return [];
      })
    ).subscribe(response => {
      subject.next(response);
      subject.complete();
    });
    return subject;
  }

  updateShipmentItems (post: any) {
    let subject = new Subject<any>;
    const referencePayloads = {
      customerReference: post.formValues.customer_reference,
      additionalReference:  post.formValues.additional_reference
    }
    this.shipmentService.updateShipmentItems(post.shipmentId, post.shipmentItems, referencePayloads).pipe(
      map((response: ResponseModel) => {
        return response;
      }), catchError (error => {
        subject.error(error.error.message || error.message || "Something went wrong processing your request");
        return [];
      })
    ).subscribe(response => {
      if (response?.success) {
        const shipment = response.data;
        this._consignments = this._consignments.map(c => {
          if (c.id == shipment.id) {
            return {
              ...c,
              shipmentItems: shipment.shipmentItems,
              customerReference: shipment.customerReference,
              additionalReference: shipment.additionalReference
            }
          }
          return c;
        });
      }
      subject.next(response);
      subject.complete();
    });
    return subject;
  }

  updatePickupDetails(post: any) {
    const subject = new Subject<any>();

    this.shipmentService.updatePickupDetails(post.shipmentId, post.pickup).pipe(
      map((response: ResponseModel) => response),
      catchError(error => {
        subject.error(error.error?.message || error.message || 'Something went wrong processing your request');
        return [];
      })
    ).subscribe((response: any) => {
      if (response?.success) {
        const patchConsignment = (c: any) => {
          if (c.id !== post.shipmentId) return c;

          const p = post.pickup || {};
          c.pickupDuration = p.pickup_duration ?? c.pickupDuration;
          c.pickup_duration = p.pickup_duration ?? c.pickup_duration;
          c.pickupTimesStrict = p.pickup_times_strict ?? c.pickupTimesStrict;
          c.earliestPickup = p.earliest_pickup ?? c.earliestPickup;
          c.latestPickup = p.latest_pickup ?? c.latestPickup;

          c.pickupStopContactInformation = p.pickup_stop_contact_information ?? c.pickupStopContactInformation;
          c.pickUpStopContactInformation = p.pickup_stop_contact_information ?? c.pickUpStopContactInformation;
          c.pickup_stop_contact_information = p.pickup_stop_contact_information ?? c.pickup_stop_contact_information;

          c.pickupNotes = p.pickup_notes ?? c.pickupNotes;
          c.pickupAdminNotes = p.pickup_admin_notes ?? c.pickupAdminNotes;
          c.pickupDriverNotes = p.pickup_driver_notes ?? c.pickupDriverNotes;

          return c;
        };

        this._consignments = this._consignments.map(patchConsignment);
        this._rawConsignments = this._rawConsignments.map(patchConsignment);
      }

      subject.next(response);
      subject.complete();
    });

    return subject;
  }

  updateDropoffDetails(post: any) {
    const subject = new Subject<any>();

    this.shipmentService.updateDropoffDetails(post.shipmentId, post.dropoff).pipe(
      map((response: ResponseModel) => response),
      catchError(error => {
        subject.error(error.error?.message || error.message || 'Something went wrong processing your request');
        return [];
      })
    ).subscribe((response: any) => {
      if (response?.success) {
        const patchConsignment = (c: any) => {
          if (c.id !== post.shipmentId) return c;

          const d = post.dropoff || {};
          c.dropoffDuration = d.dropoff_duration ?? c.dropoffDuration;
          c.dropoff_duration = d.dropoff_duration ?? c.dropoff_duration;
          c.dropoffTimesStrict = d.dropoff_times_strict ?? c.dropoffTimesStrict;
          c.earliestDropOff = d.earliest_dropoff ?? c.earliestDropOff;
          c.latestDropOff = d.latest_dropoff ?? c.latestDropOff;

          c.dropoffStopContactInformation = d.dropoff_stop_contact_information ?? c.dropoffStopContactInformation;
          c.dropOffStopContactInformation = d.dropoff_stop_contact_information ?? c.dropOffStopContactInformation;
          c.dropoff_stop_contact_information = d.dropoff_stop_contact_information ?? c.dropoff_stop_contact_information;

          c.dropOffNotes = d.dropoff_notes ?? c.dropOffNotes;
          c.dropOffAdminNotes = d.dropoff_admin_notes ?? c.dropOffAdminNotes;
          c.dropOffDriverNotes = d.dropoff_driver_notes ?? c.dropOffDriverNotes;

          return c;
        };

        this._consignments = this._consignments.map(patchConsignment);
        this._rawConsignments = this._rawConsignments.map(patchConsignment);
      }

      subject.next(response);
      subject.complete();
    });

    return subject;
  }

  publishShiftConsignment (data: any) {
    let subject = new Subject<any>;
    this.shipmentService.publishShift(data).pipe(
      map((response: ResponseModel) => {
        if (response.success) {
          this._consignments = this._consignments.map(consignment => {
            if (consignment.id == data.id) {
              consignment.publishedRoles = response.data.publishedRoles;
              consignment.publishedVehicles = response.data.publishedVehicles;
            }
            return consignment;
          });

          this._rawConsignments = this._rawConsignments.map(consignment => {
            if (consignment.id == data.id) {
              consignment.publishedRoles = response.data.publishedRoles;
              consignment.publishedVehicles = response.data.publishedVehicles;
            }
            return consignment;
          });
        }
        return response;
      }), catchError(error => {
        subject.error(error.error.message || error.message || "Something went wrong processing your request.");
        return [];
      })
    ).subscribe(response => {
      subject.next(response);
      subject.complete();
    });
    return subject;
  }

  publishShift (data: any) {
    let subject = new Subject<any>;
    this.shiftService.publishShift(data).pipe(
      map((response: ResponseModel) => {
        const responseData = response.data;
        this._consignments = this._consignments.map(consignment => {
          if (consignment.id == data.id && consignment.shiftId == data.shift_id) {
            consignment.shift.published_roles = responseData.publishedRoles;
            consignment.shift.published_vehicles = responseData.publishedVehicles;
          }
          return consignment;
        })
        return response;
      }), catchError(error => {
        subject.error(error.error.message || error.message || "Something went wrong processing your request.");
        return [];
      })
    ).subscribe(response => {
      subject.next(response);
      subject.complete();
    });
    return subject;
  }

  calculateConsignmentRates (id: number = 0) {
    let subject = new Subject<any>;
    this.shipmentService.calculateConsignmentRates(id).pipe(
      map((response: ResponseModel) => {
        if (response.success) {
          this._consignments = this._consignments.map(consignment => {
            if (consignment.id == id) {
              consignment.consignmentContractors = response.data.consignment_contractors;
            }
            return consignment;
          });

          this._rawConsignments = this._rawConsignments.map(consignment => {
            if (consignment.id == id) {
              consignment.consignmentContractors = response.data.consignment_contractors;
            }
            return consignment;
          });

        }
        return response;
      }), catchError(error => {
        subject.error(error.error.message || error.message || "Something went wrong processing your request.");
        return [];
      })
    ).subscribe(response => {
      subject.next(response);
      subject.complete();
    });
    return subject;
  }

  /**
   * Changes the charge or pay rate scope independently, then recalculates both
   * rates from the new scope. Chains the recalculation so the caller gets the
   * final consignment_contractors back directly — needed so an open Price
   * Breakdown modal can refresh in place.
   */
  updateContractorRateScope (consignmentId: number = 0, assigneeId: number = 0, side: 'charge' | 'pay' = 'charge', paidTypeId: number = 0) {
    let subject = new Subject<any>;
    const field = side === 'charge' ? 'charge_paid_type_id' : 'pay_paid_type_id';
    this.shipmentService.updateContractorRateScope(assigneeId, {[field]: paidTypeId}).pipe(
      switchMap((response: ResponseModel) => response.success ? this.shipmentService.calculateConsignmentRates(consignmentId) : of(response)),
      map((response: ResponseModel) => {
        if (response.success && response.data?.consignment_contractors) {
          this._consignments = this._consignments.map(consignment => {
            if (consignment.id == consignmentId) {
              consignment.consignmentContractors = response.data.consignment_contractors;
            }
            return consignment;
          });
          this._rawConsignments = this._rawConsignments.map(consignment => {
            if (consignment.id == consignmentId) {
              consignment.consignmentContractors = response.data.consignment_contractors;
            }
            return consignment;
          });
        }
        return response;
      }), catchError(error => {
        subject.error(error.error.message || error.message || "Something went wrong processing your request.");
        return [];
      })
    ).subscribe(response => {
      subject.next(response);
      subject.complete();
    });
    return subject;
  }

  calculateShiftRates (shiftAssigneeIds: number[] = []) {
    let subject = new Subject<any>;
    this.shiftService.bulkCalculateRates(shiftAssigneeIds).pipe(
      map((response: ResponseModel) => {
        if (response.success && response.data) {
          const updatedById = new Map<number, any>(response.data.map((item: any) => [item.id, item]));
          const mergeShiftAssignees = (consignment: any) => {
            if (!consignment.shift?.assigned_contractors) return consignment;
            const assigned_contractors = consignment.shift.assigned_contractors.map((obj: any) => {
              const updated = updatedById.get(obj.id);
              return updated ? { ...obj, transaction_charge_rate: updated.transaction_charge_rate, transaction_pay_rate: updated.transaction_pay_rate } : obj;
            });
            return { ...consignment, shift: { ...consignment.shift, assigned_contractors } };
          };
          this._consignments = this._consignments.map(mergeShiftAssignees);
          this._rawConsignments = this._rawConsignments.map(mergeShiftAssignees);
        }
        return response;
      }), catchError(error => {
        subject.error(error.error.message || error.message || "Something went wrong processing your request.");
        return [];
      })
    ).subscribe(response => {
      subject.next(response);
      subject.complete();
    });
    return subject;
  }

  onAuthoriseConsignment (post: any) {
    let subject = new Subject<any>;
    this.shipmentService.bulkAuthorisation(post).pipe(
      map((response: ResponseModel) => {
        this._consignments = this._consignments.map(consignment => {
          let isAuthorised = consignment.authorised;
          if (post.authorise.includes(consignment.id)) {
            isAuthorised = true;
          }
          if (post.unauthorise.includes(consignment.id)) {
            isAuthorised = false;
          }
          return {
            ...consignment,
            authorised: isAuthorised
          };
        });

        this._rawConsignments = this._rawConsignments.map(consignment => {
          let isAuthorised = consignment.authorised;
          if (post.authorise.includes(consignment.id)) {
            isAuthorised = true;
          }
          if (post.unauthorise.includes(consignment.id)) {
            isAuthorised = false;
          }
          return {
            ...consignment,
            authorised: isAuthorised
          };
        });
        return response;
      }), catchError(error => {
        subject.error(error.error.message || error.message || "Something went wrong processing your request.");
        return [];
      })
    ).subscribe(response => {
      subject.next(response);
      subject.complete();
    });
    return subject;
  }

  private formatTravelTime(totalSeconds: any): string {
    if (!totalSeconds) return '0min';
    const s = Math.round(Number(totalSeconds));
    if (!Number.isFinite(s) || s <= 0) return '0min';
    const m = Math.round(s / 60);
    if (m <= 0) return '0min';
    if (m < 60) return `${m}${m === 1 ? 'min' : 'mins'}`;
    const h = m === 60 ? 1 : Math.floor(m / 60) + (m % 60 >= 30 ? 1 : 0);
    return `${h}${h === 1 ? 'hr' : 'hrs'}`;
  }

  private formatTravelDistanceKm(distanceMeters: any): string {
    if (!distanceMeters) return '0km';
    const meters = Number(distanceMeters);
    if (!Number.isFinite(meters) || meters <= 0) return '0km';
    const kmRaw = meters / 1000;
    const km = Math.floor(kmRaw + 0.5); // >= .5 up, < .5 nochange
    return `${km}km`;
  }

  /* Combine response data of shipment contractor rates to existsing consignments data */
  getConsignmentContractorRates (ids: number[] = []) {
    let subject = new Subject<any>();
    if (ids.length === 0) {
      return of(false);
    }
    this.shipmentService.getContractorRates(ids).pipe(
      map((response: ResponseModel) => {
        return response;
      }),catchError(error => {
        subject.error(error)
        return [];
      })
    ).subscribe(response => {
      if (response.success) {
        const transactionRates: any[] = response.data;
        this._consignments = this.consignments.map(consignment => {
          const shipmentContractorRates = transactionRates.find(rate => rate.shipment_id == consignment.id);
          if (shipmentContractorRates) {
            consignment.consignmentContractors = shipmentContractorRates.contractors;
          }
          return consignment;
        });

        this._rawConsignments = this.consignments.map(consignment => {
          const shipmentContractorRates = transactionRates.find(rate => rate.shipment_id == consignment.id);
          if (shipmentContractorRates) {
            consignment.consignmentContractors = shipmentContractorRates.contractors;
          }
          return consignment;
        });

      }
      subject.next(response);
      subject.complete();
    });
    return subject;
  }

  /* This only use when fetching consignment table data */
  attachConsignmentShift (consignmentIds: number[] = []) {
    let subject = new Subject();
    if (consignmentIds.length === 0) {
      return of(false);
    }
    this.shipmentService.getShiftWithContractor(consignmentIds).pipe(
      map((response: ResponseModel) => {
        return response;
      }),
      catchError(error => {
        subject.error(error);
        return [];
      })
    ).subscribe(response => {
      if(response.success) {
        const shiftData: any[] = response.data;
        this._consignments = this._consignments.map(consignment => {
          const _consignmentShift = shiftData.find(consignmentShift => consignmentShift.id == consignment.id);
          if (_consignmentShift) {
            consignment.shift = _consignmentShift.shift;
            consignment.authorised = _consignmentShift.shift?.assigned_contractors.some((contractor:any) => contractor.authorised) ?? false;
          }
          return consignment;
        });

        this._rawConsignments = this._rawConsignments.map(consignment => {
          const _consignmentShift = shiftData.find(consignmentShift => consignmentShift.id == consignment.id);
          if (_consignmentShift) {
            consignment.shift = _consignmentShift.shift;
            consignment.authorised = _consignmentShift.shift?.assigned_contractors.some((contractor:any) => contractor.authorised) ?? false;
          }
          return consignment;
        });

      }
      subject.next(response);
      subject.complete();
    });
    return subject;
  }

  fetchPickupDropOff (consignmentIds: number[] = []) {
    let subject = new Subject();
    this.shipmentService.getPickupDropoff(consignmentIds).pipe(
      map((response: ResponseModel) => {
        return response;
      }),
      catchError(error => {
        subject.error(error);
        return [];
      })
    ).subscribe(response => {
      if (response.success) {
        consignmentIds.forEach(consignmentId => {
          this.reservedDropOffPickupDetails.add(consignmentId);
          this._consignments = this._consignments.map((consignment:any) => {
            if (consignment.id == consignmentId) {  
              const data = response.data.find((_data:any) => _data.id == consignmentId);
              consignment.pickupStop = data.pick_up_stop;
              consignment.dropoffStop = data.drop_off_stop;
              consignment.shipmentItems = data.shipment_items;
              consignment.travelSummary = this.generateTravelSummaryStr(consignment);
              consignment.stateName = this.commonResourceService.states.find(state => state.id == consignment.pickupStop.state_id)?.abbr || ''
              consignment.hasDGItem = data.shipment_items.some((item:any) => item?.dg_details ?? false);
            }
            return consignment;
          });
        });
      }
      subject.next(response);
      subject.complete();
    });
    return subject;
  }

  hasDropOffPickUpDetails (id: number = 0) {
    return this.reservedDropOffPickupDetails.has(id);
  }

  getOtherDetails (consignmentIds: number[] = []) {
    let subj = new Subject();
    this.shipmentService.getOtherDetails(consignmentIds).pipe(
      map((response: ResponseModel) => {
        return response;
      }),
      catchError(error => {
        subj.error(error);
        return [];
      })
    ).subscribe(response => {
      if (response.success) {
        const dataMap = new Map(response.data.map((item: any) => [item.id, item]));
        const updateConsignment = (c: any) => {
          if (!consignmentIds.includes(c.id)) return c;
          
          const data:any = dataMap.get(c.id);
          if (!data) return c;

          return {
            ...c,
            consignmentContractors: data.consignment_contractors,
            publishedRoles: data.published_roles,
            publishedVehicles: data.published_vehicles,
            macshipCharge: data.machship_charge_rates ?? c.macshipCharge,
            macshipPay: data.machship_pay_rates ?? c.macshipPay,
          };
        };
        this._consignments = this._consignments.map(updateConsignment);
        this._rawConsignments = this._rawConsignments.map(updateConsignment);
      }
      subj.next(response);
      subj.complete();
    });

    return subj;
  }

  attachShipment (newConsignments: any[] = []) {
    if (!newConsignments.length) return;
    const consignments = newConsignments
    .map(obj => ConsignmentModel.fromJson(obj))
    .map(obj => this.normalizeConsignment(obj))
    .map(obj => {
      return Object.assign({
        ...obj,
        clientName: `${obj.clientInfo.customer_profile.business_name} - ${obj.clientInfo.name}`,
        stopStatusName: this.commonResourceService.stopStatus.find(stopStatus => stopStatus.id == obj.stopStatusId)?.name || '',
        stateName: this.commonResourceService.states.find(state => state.id == obj.pickUpStop.state_id)?.abbr || '',
        travelSummary: this.generateTravelSummaryStr(obj),
        machshipCharge: obj.machship_charge_rates,
        machshipPay: obj.machship_pay_rates
      });
    });

    this._consignments = [
      ...this._consignments,
      ...consignments
    ];
  }

  public get availableMachshipTransaction () {
    return this._availableMachshipTransaction;
  }

  setAvailableMachship (machship: any, unShift: boolean = false) {
    this._availableMachshipTransaction = unShift ? [machship,...this._availableMachshipTransaction] : [...this._availableMachshipTransaction,machship];
  }

  removeFromAvailableMachshipNotif (consignmentId: number = 0) {
    this._availableMachshipTransaction = this._availableMachshipTransaction.filter(machship => machship.consignmentId != consignmentId);
  }

  updateConsignment (data: any, type: string = '') {
    switch (type) {
      case "stop_status":
        const newStopStatus = {
          stopStatusId: data.stop_status_id,
          stopStatusName: this.commonResourceService.stopStatus.find(status => status.id == data.stop_status_id)?.name || ''
        }
        this._consignments = this._consignments.map(consignment => {
          if (consignment.id == data.id) {
            return {
              ...consignment,
              ...newStopStatus
            };
          }
          return consignment;
        });

        this._rawConsignments = this._rawConsignments.map(consignment => {
          if (consignment.id == data.id) {
            return {
              ...consignment,
              ...newStopStatus
            };
          }
          return consignment;
        });
      break;
      case "service_type":
      case "authorisation":
        let newData: any = {
          serviceTypeId: data.service_type_id,
          serviceTypeName: this.commonResourceService.serviceTypes.find(service => service.id == data.service_type_id)?.name || ''
        };
        if (type == 'authorisation') {
          newData = {
            authorised: data.authorised
          }
        }
        this._consignments = this._consignments.map(consignment => {
          if (consignment.id == data.id) {
            return {
              ...consignment,
              ...newData
            };
          }
          return consignment;
        });
        this._rawConsignments = this._rawConsignments.map(consignment => {
          if (consignment.id == data.id) {
            return {
              ...consignment,
              ...newData
            };
          }
          return consignment;
        });
      break;
      case "jobs_assign_contractor":
      case "jobs_remove_contractor":
        this._consignments = this._consignments.map(consignment => {
          if (consignment.id == data.id) {
            return {
              ...consignment,
              consignmentContractors: data.contractors
            }
          }
          return consignment;
        });
        this._rawConsignments = this._rawConsignments.map(consignment => {
          if (consignment.id == data.id) {
            return {
              ...consignment,
              consignmentContractors: data.contractors
            }
          }
          return consignment;
        });
      break;
      case "calculate_rates":
        this._consignments = this._consignments.map(consignment => {
          if (consignment.id == data.id) {
            consignment.consignmentContractors = data.contractors;
          }
          return consignment;
        });
        this._rawConsignments = this._rawConsignments.map(consignment => {
          if (consignment.id == data.id) {
            consignment.consignmentContractors = data.contractors;
          }
          return consignment;
        });
      break;
      case "edit_pickup":
        const pickupData = {
          pickUpDuration: data.pickup_duration,
          pickUpTimesStrict: data.pickup_times_strict,
          earliestPickup: data.earliest_pickup_datetime,
          latestPickup: data.latest_pickup_datetime,
          pickUpStopContactInformmation: data.pickup_contact_information,
          pickupNotes: data.pickup_notes,
          pickupAdminNotes: data.pickup_admin_notes,
          pickupStop: data.pickup_stop
        }
        this._consignments = this._consignments.map(consignment => {
          if (consignment.id == data.id) {
            consignment = {
              ...consignment,
              ...pickupData
            }
          }
          return consignment;
        });
        this._rawConsignments = this._rawConsignments.map(consignment => {
          if (consignment.id == data.id) {
            consignment = {
              ...consignment,
              ...pickupData
            }
          }
          return consignment;
        });
      break;
      case "edit_dropoff":
        const dropOffData = {
          dropOffDuration: data.dropoff_duration,
          dropOffTimesStrict: data.dropoff_times_strict,
          earliestDropOff: data.earliest_dropoff_datetime,
          latestDropOff: data.latest_dropoff_datetime,
          dropOffStopContactInformation: data.dropoff_contact_information,
          dropOffNotes: data.dropoff_notes,
          dropOffAdminNotes: data.dropoff_admin_notes,
          dropoffStop: data.dropoff_stop
        }
        this._consignments = this._consignments.map(consignment => {
          if (consignment.id == data.id) {
            consignment = {
              ...consignment,
              ...dropOffData
            }
          }
          return consignment;
        });
        this._rawConsignments = this._rawConsignments.map(consignment => {
          if (consignment.id == data.id) {
            consignment = {
              ...consignment,
              ...dropOffData
            }
          }
          return consignment;
        });
      break;
      case "shipment_items":
        this._consignments = this._consignments.map(consignment => {
          if (data.id == consignment.id) {
            return {
              ...consignment,
              shipmentItems: data.shipment_items
            }
          }
          return consignment;
        });
        this._rawConsignments = this._rawConsignments.map(consignment => {
          if (data.id == consignment.id) {
            return {
              ...consignment,
              shipmentItems: data.shipment_items
            }
          }
          return consignment;
        });
      break;
      case "delete_consignment":
        this._consignments = this._consignments.filter(consignment => {
          return consignment.id != data.id;
        });
        this._rawConsignments = this._rawConsignments.filter(consignment => {
          return consignment.id != data.id;
        });
      break;
      case "proof_of_delivery":
        this._consignments = this._consignments.map(consignment => {
          if (data.id == consignment.id) {
            return {
              ...consignment,
              proofOfDelivery: data.proof_of_delivery
            }
          }
          return consignment;
        });
        this._rawConsignments = this._rawConsignments.map(consignment => {
          if (data.id == consignment.id) {
            return {
              ...consignment,
              proofOfDelivery: data.proof_of_delivery
            }
          }
          return consignment;
        });
      break;
      case "remove_consignment_shift":
        this._consignments = this._consignments.map(consignment => {
          if (data.id == consignment.id) {
            return {
              ...consignment,
              shiftId: 0,
              shift: null
            }
          }
          return consignment;
        });
        this._rawConsignments = this._rawConsignments.map(consignment => {
          if (data.id == consignment.id) {
            return {
              ...consignment,
              shiftId: 0,
              shift: null
            }
          }
          return consignment;
        });
      break;
      case "shifts_assign_contractor":
      case "shifts_remove_contractor":
        this._consignments = this._consignments.map(consignment => {
          if (consignment.id == data.id && consignment.shift) {
            consignment.shift = {
              ...consignment.shift,
              assigned_contractors: data.contractors
            }
          }
          return consignment;
        });
        this._rawConsignments = this._rawConsignments.map(consignment => {
          if (consignment.id == data.id && consignment.shift) {
            consignment.shift = {
              ...consignment.shift,
              assigned_contractors: data.contractors
            }
          }
          return consignment;
        });
      break;
    }
  }

  updatePriceBreakdown (priceBreakdownData: any) {
    let subject = new Subject;
    const data = priceBreakdownData;
    /* Same source priority as getSellPrice()/getCostPrice() and openPriceBreakdownModal() (an assigned
       driver's own charge/pay rate takes precedence over the shipment-level Machship rate) - the id sent
       here must match whichever record the modal actually displayed/edited, or the save would silently
       update the wrong rate record. */
    const driver = data.consignment.consignmentContractors?.find((item: any) => item.vehicle_type_id);
    const chargeId = driver?.transaction_charge_rate?.id ?? data.consignment.macshipCharge.id;
    const payId = driver?.transaction_pay_rate?.id ?? data.consignment.macshipPay.id;
    /* activePriceBreakdownData's row fields (baseTotalSell, fuelSell, ...) are ex. GST - the units
       the Price Breakdown modal displays/edits them in (see openPriceBreakdownModal/
       reCalculatePriceBreakdown) - and every "*_calculated" column the API stores them into is ALSO
       ex-GST, unconverted (see ShipmentController::saveShipmentPriceTotals and
       ConsignmentsController::updatePriceBreakdown, which both just persist whatever's sent), so each
       row is sent through as-is - what the modal displays is what gets saved. totalSell/totalCost are
       the correct GST-inclusive grand total and are likewise sent through as-is.

       fuelRaw/regionalRaw/... (CSE-824) are the RAW %/$ number the modal's input actually holds for a
       percentage-capable row (e.g. 22.40, not the $359.52 it resolves to) - sent alongside the
       resolved dollar amount above so the backend can update the row's stored "22.40%"/"$75.00"
       display string with the new number without changing its existing unit. null for rows edited as
       plain dollars, where the *_calculated column is already the whole story. */
    const num = (value: number) => Number(value) || 0;
    const rawOrNull = (value: number) => (value === undefined || value === null) ? null : Number(value) || 0;
    const payloads = {
      id: data.consignment.id,
      charge: {
        id: chargeId,
        base: num(data.baseTotalSell),
        fuel: num(data.fuelSell),
        fuelRaw: rawOrNull(data.fuelRawSell),
        regional: num(data.regionalSell),
        regionalRaw: rawOrNull(data.regionalRawSell),
        saturday: num(data.saturdaySell),
        saturdayRaw: rawOrNull(data.saturdayRawSell),
        sunday: num(data.sundaySell),
        sundayRaw: rawOrNull(data.sundayRawSell),
        publicHoliday: num(data.publicHolidaySell),
        publicHolidayRaw: rawOrNull(data.publicHolidayRawSell),
        afterHours: num(data.afterHoursSell),
        afterHoursRaw: rawOrNull(data.afterHoursRawSell),
        tailgate: num(data.tailgateSell),
        tailgateRaw: rawOrNull(data.tailgateRawSell),
        jockey: num(data.jockeySell),
        toll: num(data.tollSell),
        total: data.totalSell
      },
      pay: {
        id: payId,
        base: num(data.baseTotalCost),
        fuel: num(data.fuelCost),
        fuelRaw: rawOrNull(data.fuelRawCost),
        regional: num(data.regionalCost),
        regionalRaw: rawOrNull(data.regionalRawCost),
        saturday: num(data.saturdayCost),
        saturdayRaw: rawOrNull(data.saturdayRawCost),
        sunday: num(data.sundayCost),
        sundayRaw: rawOrNull(data.sundayRawCost),
        publicHoliday: num(data.publicHolidayCost),
        publicHolidayRaw: rawOrNull(data.publicHolidayRawCost),
        afterHours: num(data.afterHoursCost),
        afterHoursRaw: rawOrNull(data.afterHoursRawCost),
        tailgate: num(data.tailgateCost),
        tailgateRaw: rawOrNull(data.tailgateRawCost),
        jockey: num(data.jockeyCost),
        toll: num(data.tollCost),
        total: data.totalCost
      }
    };
    this.shipmentService.updatePriceBreakdown(payloads).pipe(
      map((response: ResponseModel) => {
        return response;
      }),
      catchError(error => {
        subject.error(error.error.message || "Something went wrong");
        return EMPTY;
      })
    ).subscribe(response => {
      if (response.success) {
        const responseData = response.data;
        /* Only the record actually saved (driver's rate if one was targeted, otherwise the Machship
           rate - see the id resolution above) should be patched locally; patching both regardless of
           which was really updated is how the list and this modal disagreed after a save. */
        /* Alongside every *_calculated column, also patch the raw "22.40%"/"$75.00" display-string
           columns (fuel_charge, tailgate_loading, ...) and Toll - openPriceBreakdownModal()'s
           parseRateType() reads those raw columns to decide whether a row is %- or $-based and what
           number to show, so leaving them stale here is what made a saved Fuel Levy % edit appear to
           "revert" the next time this modal was reopened, even though the calculated total was
           correct. See CSE-824. */
        const chargePatch = {
          base_rate_delivery_calculated: responseData.chargeRate.baseCalculated,
          fuel_charge_calculated: responseData.chargeRate.fuelCalculated,
          fuel_charge: responseData.chargeRate.fuel,
          regional_charge_calculated: responseData.chargeRate.regionalCalculated,
          regional_charge: responseData.chargeRate.regional,
          saturday_loading_calculated: responseData.chargeRate.saturdayCalculated,
          saturday_loading: responseData.chargeRate.saturday,
          sunday_loading_calculated: responseData.chargeRate.sundayCalculated,
          sunday_loading: responseData.chargeRate.sunday,
          public_holiday_charge_calculated: responseData.chargeRate.publicHolidayCalculated,
          public_holiday_charge: responseData.chargeRate.publicHoliday,
          afterhours_charge_calculated: responseData.chargeRate.afterHoursCalculated,
          afterhours_charge: responseData.chargeRate.afterHours,
          tailgate_loading_calculated: responseData.chargeRate.tailgateCalculated,
          tailgate_loading: responseData.chargeRate.tailgate,
          jockey_charge_calculated: responseData.chargeRate.jockeyCalculated,
          toll_road_calculated: responseData.chargeRate.tollCalculated,
          toll_road_rate: responseData.chargeRate.toll,
          total_charge: responseData.chargeRate.total
        };
        const payPatch = {
          base_rate_delivery_calculated: responseData.payRate.baseCalculated,
          fuel_charge_calculated: responseData.payRate.fuelCalculated,
          fuel_charge: responseData.payRate.fuel,
          regional_charge_calculated: responseData.payRate.regionalCalculated,
          regional_charge: responseData.payRate.regional,
          saturday_loading_calculated: responseData.payRate.saturdayCalculated,
          saturday_loading: responseData.payRate.saturday,
          sunday_loading_calculated: responseData.payRate.sundayCalculated,
          sunday_loading: responseData.payRate.sunday,
          public_holiday_charge_calculated: responseData.payRate.publicHolidayCalculated,
          public_holiday_charge: responseData.payRate.publicHoliday,
          afterhours_charge_calculated: responseData.payRate.afterHoursCalculated,
          afterhours_charge: responseData.payRate.afterHours,
          tailgate_loading_calculated: responseData.payRate.tailgateCalculated,
          tailgate_loading: responseData.payRate.tailgate,
          jockey_charge_calculated: responseData.payRate.jockeyCalculated,
          toll_road_calculated: responseData.payRate.tollCalculated,
          toll_road_rate: responseData.payRate.toll,
          total_pay: responseData.payRate.total
        };

        this._consignments = this._consignments.map(consignment => {
          if (consignment.id != responseData.id) return consignment;
          const driver = consignment.consignmentContractors?.find((item: any) => item.vehicle_type_id);
          if (driver) {
            return {
              ...consignment,
              consignmentContractors: consignment.consignmentContractors.map(c => {
                if (c !== driver) return c;
                return {
                  ...c,
                  transaction_charge_rate: { ...c.transaction_charge_rate, ...chargePatch },
                  transaction_pay_rate: { ...c.transaction_pay_rate, ...payPatch }
                };
              })
            };
          }
          return {
            ...consignment,
            macshipCharge: { ...consignment.macshipCharge, ...chargePatch },
            macshipPay: { ...consignment.macshipPay, ...payPatch }
          };
        });
        subject.next(response);
        subject.complete();
      }
    });
    return subject;
  }

  adjustContractorRateAmount (data: any) {
    return this.shipmentService.adjustContractorRateAmount(data);
  }

  /** Live "auto-calculate" preview for the Jobs Price Breakdown modal — recalculates line items from an edited raw rate field, without persisting anything. */
  previewContractorRateBreakdown (contractorId: number = 0, side: 'charge' | 'pay' = 'charge', rate: any = {}) {
    return this.shipmentService.previewContractorRateBreakdown(contractorId, side, rate);
  }

  /** Full change history for a shipment (Consignment/Job) record — powers the row-level "View Logs" button. */
  getShipmentLogs (shipmentId: number) {
    return this.shipmentService.getShipmentLogs(shipmentId);
  }

  /** Full change history for a Job's Price Breakdown rate row — powers the Price Breakdown modal's own "View Logs" button (CSE-824). */
  getPriceBreakdownLogs (thChargeId?: number, thPayId?: number) {
    return this.shipmentService.getPriceBreakdownLogs(thChargeId, thPayId);
  }

  reSyncMachshipRates (shipmentId: number) {
    let subj = new Subject;
    this.shipmentService.reSyncMachshipRates(shipmentId).pipe(
      map((response: ResponseModel) => {
        return response;
      }),
      catchError(error => {
        subj.error(error?.error?.message || "Something went wrong processing your request.")
        return EMPTY;
      })
    ).subscribe({
      next: (response: ResponseModel) => {
        if (response.success && response.data) {
          this._consignments = this._consignments.map(consignment => {
            if (consignment.id == shipmentId) {
              consignment.consignmentContractors = response.data?.contractors ?? consignment.consignmentContractors;
              consignment.macshipCharge = response.data?.machshipRates?.charge ?? consignment.macshipCharge;
              consignment.macshipPay = response.data?.machshipRates?.pay ?? consignment.macshipPay;
            }
            return consignment;
          });
          this._rawConsignments = this._rawConsignments.map(consignment => {
            if (consignment.id == shipmentId) {
              consignment.consignmentContractors = response.data?.contractors ?? consignment.consignmentContractors;
              consignment.macshipCharge = response.data?.machshipRates?.charge ?? consignment.macshipCharge;
              consignment.macshipPay = response.data?.machshipRates?.pay ?? consignment.macshipPay;
            }
            return consignment;
          });
        }
        subj.next(response);
        subj.complete();
      }
    });

    return subj;
  }

  filterByStatHeader (statusId: number = 0, consignments: any[] = []) {
    if (statusId == 0) return consignments;
    return consignments.filter(c => {
      return statusId == c.stopStatusId;
    });
  }

  generateLabels (id: number = 0) {
    let subj = new Subject;
    this.shipmentService.generateLabels(id).pipe(
      map((res: ResponseModel) => res),
      catchError(() => EMPTY)
    ).subscribe({
      next: (response:any) => {
        const c = this.consignments.find(cn => cn.id == id);
        if (c) {
          const base64DataUrl = response.url;
          const fileName = `${Math.floor(Date.now() / 1000)}_${c.consignmentNumber}.pdf`;
          this.downloadBase64AsBlob(base64DataUrl, fileName);
          this.notyf.notifySuccess('Your labels has finally arrived! save it now.');
          subj.next(response);
          subj.complete();
        }
      }
    });

    return subj;
  }

  downloadBase64AsBlob (base64DataUrl: string, fileName: string =  'labels.pdf') {
    const parts = base64DataUrl.split(';base64,');
    const mimeType = parts[0].split(':')[1];
    const rawBase64 = window.atob(parts[1]);

    const uInt8Array = new Uint8Array(rawBase64.length);
    for (let i = 0; i < rawBase64.length; i++) {
      uInt8Array[i] = rawBase64.charCodeAt(i);
    }

    const blob = new Blob([uInt8Array], { type: mimeType });
    const objectUrl = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
  }

  generateManifestLabels (id: number = 0) {
    let subj = new Subject;
    this.shipmentService.generateManifestLabels(id).pipe(
      map((res: ResponseModel) => res),
      catchError(() => EMPTY)
    ).subscribe({
      next: (response:any) => {
        const c = this.consignments.find(cn => cn.id == id);
        if (c) {
          const base64DataUrl = response.url;
          const fileName = `${response.fileName}.pdf`;
          this.downloadBase64AsBlob(base64DataUrl, fileName);
          this.notyf.notifySuccess('Your Manifest-information has finally arrived! save it now.');
          subj.next(response);
          subj.complete();
        }
      }
    });

    return subj;
  }

  generateConsignmentNotes (id: number = 0) {
    let subj = new Subject;
    this.shipmentService.generateConsignmentNotes(id).pipe(
      map((res: ResponseModel) => res),
      catchError(() => EMPTY)
    ).subscribe({
      next: (response:any) => {
        const c = this.consignments.find(cn => cn.id == id);
        if (c) {
          const base64DataUrl = response.url;
          const fileName = `${response.fileName}.pdf`;
          this.downloadBase64AsBlob(base64DataUrl, fileName);
          this.notyf.notifySuccess('Your Consignment-notes has finally arrived! save it now.');
          subj.next(response);
          subj.complete();
        }
      }
    });

    return subj;
  }

  downloadAllAttachments (id: number = 0) {
    let subj = new Subject;
    this.shipmentService.downloadAllAttachments(id).pipe(
      map((res: ResponseModel) => res),
      catchError(() => EMPTY)
    ).subscribe({
      next: (response:any) => {
        const c = this.consignments.find(cn => cn.id == id);
        if (c) {
          const base64DataUrl = response.url;
          const fileName = `${response.fileName}.zip`;
          this.downloadBase64AsBlob(base64DataUrl, fileName);
          this.notyf.notifySuccess('Your All Attachments has finally arrived! save it now.');
          subj.next(response);
          subj.complete();
        }
      }
    });

    return subj;
  }

  bulkSendEmailAttachments (shipmentId: number = 0, emails: string[] = []) {
    return this.shipmentService.bulkSendEmailAttachments(shipmentId, emails);
  }

  sendDriverManifestInfo (shipmentId: number = 0, email: string = '') {
    return this.shipmentService.sendDriverManifestInfo(shipmentId, email)
  }

}
